import tw from "twin.macro";

import { useEffect, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";

import { Chip } from "@mui/material";

import { pick } from "lodash/fp";

import { useChannelsListQuery } from "@features/channels";
import { useTerritoriesListQuery } from "@features/territories";
import { ToggleButtons } from "@features/ui";
import { StyledFormLabel } from "@utils/forms/ControlledInputs";

import { ControlledAutocompleteInput } from "../../../utils/forms";
import BlockCard from "../../ui/BlockCard";

const Loading = tw.div`h-10 rounded-md bg-neutral-100 animate-pulse`;
const SelectTerritories = () => {
  const { data: allTerritories, isLoading } = useTerritoriesListQuery({});
  const { control, setValue } = useFormContext();

  const territories = useWatch({ name: "territories" });
  const isTerritoryExclusive: boolean = useWatch({
    name: "isTerritoryExclusive",
  });

  const territoryOptions = useMemo(
    () => allTerritories?.map(pick(["id", "name"])) ?? [],
    [allTerritories]
  );

  const handleRemoveTerritory = (id: string) => {
    setValue(
      "territories",
      territories.filter((terr) => terr.id !== id),
      { shouldDirty: true }
    );
  };

  useEffect(() => {
    if (!isTerritoryExclusive && territoryOptions.length > 0) {
      setValue("territories", territoryOptions);
    }
  }, [isTerritoryExclusive, territoryOptions, setValue]);

  if (!allTerritories || isLoading) return <Loading />;

  return (
    <div tw="space-y-3">
      <div>
        <StyledFormLabel>Territory Visibility</StyledFormLabel>
        <ToggleButtons
          tw="bg-neutral-100 shadow-none"
          options={[
            { title: "All Territories", value: "false" },
            { title: "Selected Territories", value: "true" },
          ]}
          onChange={(value) =>
            setValue("isTerritoryExclusive", value === "true", {
              shouldDirty: true,
            })
          }
          value={isTerritoryExclusive ? "true" : "false"}
          tabProps={{ className: "flex-1" }}
        />
      </div>
      {isTerritoryExclusive && (
        <>
          <ControlledAutocompleteInput
            multiple
            label="Territories"
            name="territories"
            control={control}
            options={territoryOptions}
            renderTags={() => null}
            disableCloseOnSelect
          />
          <div tw="flex flex-wrap gap-2">
            {territories?.map((territory) => (
              <Chip
                size="small"
                key={territory.id}
                label={territory.name}
                onDelete={() => handleRemoveTerritory(territory.id)}
              />
            ))}
            {territories.length === 0 && (
              <div tw="text-sm text-neutral-700">No Territories selected</div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const SelectChannel = ({ handleChangeChannel }) => {
  const { control } = useFormContext();
  const { data: channels } = useChannelsListQuery();

  if (!channels) return <Loading />;

  return (
    <ControlledAutocompleteInput
      label="Channel"
      disableClearable
      options={channels}
      control={control}
      name="channelId"
      tw="w-full"
      onChange={handleChangeChannel}
    />
  );
};

const Visibility = ({ handleChangeChannel }) => {
  const { currentChannelId } = useSelector((state: any) => state.currentUser);

  return (
    <BlockCard title="Visibility" tw="space-y-3">
      {currentChannelId && (
        <SelectChannel handleChangeChannel={handleChangeChannel} />
      )}
      <SelectTerritories />
    </BlockCard>
  );
};

export default Visibility;
